// Chakra Imports
import {
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useColorModeValue,
  Box,
  useToast,
  useDisclosure,
  Img,
  Badge,
  VStack,
  HStack,
  Portal,
} from '@chakra-ui/react';
import { SidebarResponsive } from 'components/sidebar/Sidebar';
import PropTypes from 'prop-types';
import React from 'react';
// Assets
import { MdNotificationsNone, MdFullscreen, MdFullscreenExit, MdSettings, MdLogout } from 'react-icons/md';
import routes from 'routes';
import axios from 'axios';
import { BASE_URL } from 'config/api';
import { useNavigate, useLocation } from 'react-router-dom'; // for redirection
import { logout, getMyUserProfile, getUnreadMessagesCounts, getMissions, markAllMessagesRead } from 'api/api'; // for logout
import MissionNonAccepteeNotification from './MissionNonAccepteeNotification';
import NotificationsModal from './NotificationsModal';
import PhoneWidget from './PhoneWidget';
import { useNavbarAction } from 'contexts/NavbarActionContext';
import { io } from 'socket.io-client';

const getSocketServerUrl = () => {
  try {
    const parsed = new URL(BASE_URL);
    return parsed.origin;
  } catch (_) {
    return BASE_URL;
  }
};

export default function HeaderLinks(props) {
  const { secondary } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { isOpen: isNotificationsModalOpen, onOpen: onNotificationsModalOpen, onClose: onNotificationsModalClose } = useDisclosure();
  const { userData: contextUserData, setUserData } = useNavbarAction();
  const [unreadMessages, setUnreadMessages] = React.useState({});
  const [notificationSound] = React.useState(new Audio('/notification.mp3'));
  const [novalideSound] = React.useState(new Audio('/novalide.mp3'));
  const [playedSoundForMessages, setPlayedSoundForMessages] = React.useState(new Set());
  const [ambulanciers, setAmbulanciers] = React.useState({});
  const [missions, setMissions] = React.useState([]);
  const [hasUserInteracted, setHasUserInteracted] = React.useState(false);
  const [isConnected, setIsConnected] = React.useState(true);
  const [realTimeNotifications, setRealTimeNotifications] = React.useState([]);
  const [unreadAlertes, setUnreadAlertes] = React.useState(0);
  const [retryCount, setRetryCount] = React.useState(0);

  const [pendingMissions, setPendingMissions] = React.useState(new Map()); // Pour suivre les missions en attente
  const [notifiedMissions, setNotifiedMissions] = React.useState(new Map()); // Map<missionId, timestamp> pour suivre les missions notifiées avec timestamp de fermeture
  const [currentlyDisplayedMissions, setCurrentlyDisplayedMissions] = React.useState(new Set()); // Set<missionId> pour suivre les notifications actuellement affichées
  const [otherMissionsButtonTimestamp, setOtherMissionsButtonTimestamp] = React.useState(null); // Timestamp de la dernière fermeture du bouton "+X autres missions"
  const [isOtherMissionsButtonDisplayed, setIsOtherMissionsButtonDisplayed] = React.useState(false); // Si le bouton est actuellement affiché
  // File d'attente des notifications: affiche une notification à la fois
  const [notificationQueue, setNotificationQueue] = React.useState([]); // Array<misionData>
  const [isShowingMissionNotification, setIsShowingMissionNotification] = React.useState(false); // Si actuellement une notification de mission est affichée
  const [user, setUser] = React.useState(null); // Informations utilisateur
  const [dossierBanner, setDossierBanner] = React.useState(null);
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 5000; // 5 seconds

  const showDossierBanner = React.useCallback((title, subtitle, onView) => {
    setDossierBanner({
      id: `${Date.now()}-${Math.random()}`,
      title,
      subtitle,
      onView: onView || null,
    });
  }, []);

  React.useEffect(() => {
    if (!dossierBanner?.id) return undefined;
    const timer = setTimeout(() => setDossierBanner(null), 7000);
    return () => clearTimeout(timer);
  }, [dossierBanner?.id]);

  // Chakra Color Mode
  let menuBg = useColorModeValue('white', 'navy.800');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('#E6ECFA', 'rgba(135, 140, 189, 0.3)');

  const [isFullscreen, setIsFullscreen] = React.useState(false);

  // Initialiser userData dès le montage du composant
  React.useEffect(() => {
    const initializeUserData = async () => {
      try {
        const res = await getMyUserProfile();
        const data = res.data;
        setUser(data);
        setUserData({
          firstname: data.prenom || "User",
          lastname: data.nom || "Profile",
          role: data.rôle || "Staff",
          photo: data.photo || null,
        });
      } catch (error) {
        console.error("Error initializing user data:", error);
      }
    };

    initializeUserData();
  }, [setUserData]);

  // Ajouter un useEffect pour détecter l'interaction utilisateur
  React.useEffect(() => {
    const handleUserInteraction = () => {
      setHasUserInteracted(true);
      // Supprimer les écouteurs après la première interaction
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
    };

    document.addEventListener('click', handleUserInteraction);
    document.addEventListener('keydown', handleUserInteraction);

    return () => {
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
    };
  }, []);

  // Précharger les sons
  React.useEffect(() => {
    notificationSound.load();
    novalideSound.load();
  }, [notificationSound, novalideSound]);

  // Appeler les fonctions au démarrage
  React.useEffect(() => {
    // Appeler immédiatement au chargement, puis après interaction utilisateur
    const initialDelay = setTimeout(() => {
      fetchNewMessagesFunc();
      if (location.pathname !== '/admin/missions/create') {
        checkNewMissions();
      }
    }, 2000); // Attendre 2 secondes après le chargement

    // Si l'utilisateur interagit avant, appeler immédiatement
    if (hasUserInteracted) {
      clearTimeout(initialDelay);
      fetchNewMessagesFunc();
      if (location.pathname !== '/admin/missions/create') {
        checkNewMissions();
      }
    }

    return () => clearTimeout(initialDelay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUserInteracted]);

  // Nettoyer les notifications existantes si on arrive sur la page de création
  React.useEffect(() => {
    if (location.pathname === '/admin/missions/create') {
      toast.closeAll();
      setNotificationQueue([]);
      setIsShowingMissionNotification(false);
    }
  }, [location.pathname, toast]);

  // Modifier la logique de polling des messages
  React.useEffect(() => {
    let interval;

    // Démarrer le polling après un court délai, même sans interaction utilisateur
    const startPolling = () => {
      interval = setInterval(() => {
        fetchNewMessagesFunc();
        // Ne pas vérifier les missions si on est sur la page de création de mission
        if (location.pathname !== '/admin/missions/create') {
          checkNewMissions();
        }
      }, 15000);
    };

    // Démarrer le polling après 3 secondes (donne le temps à l'app de se charger)
    const pollingDelay = setTimeout(() => {
      startPolling();
    }, 3000);

    return () => {
      clearTimeout(pollingDelay);
      if (interval) {
        clearInterval(interval);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playedSoundForMessages, ambulanciers, isConnected, retryCount, location.pathname]);

  // Effet qui consomme la file d'attente et affiche une notification à la fois
  React.useEffect(() => {
    // Ne pas afficher de notifications sur la création mission
    // ni sur toutes les pages dossier de régulation.
    if (
      location.pathname === '/admin/missions/create' ||
      location.pathname.startsWith('/admin/dossier-regulation')
    ) return;

    if (isShowingMissionNotification) return; // Déjà en cours d'affichage
    if (notificationQueue.length === 0) return;

    const missionData = notificationQueue[0];
    if (!missionData) return;

    setIsShowingMissionNotification(true);

    const toastId = `mission-non-acceptee-${missionData.id}`;

    const handleClose = () => {
      // Marquer comme notifié
      setNotifiedMissions(prev => {
        const newMap = new Map(prev);
        newMap.set(missionData.id, Date.now()); // Enregistrer le moment de la fermeture
        return newMap;
      });

      // Enlever de currentlyDisplayedMissions si présent
      setCurrentlyDisplayedMissions(prev => {
        const newSet = new Set(prev);
        newSet.delete(missionData.id);
        return newSet;
      });

      // Retirer l'élément courant de la file
      setNotificationQueue(prev => prev.slice(1));
      setIsShowingMissionNotification(false);
    };

    const handleCloseAll = () => {
      // Marquer toutes les missions de la queue comme notifiées
      setNotifiedMissions(prev => {
        const newMap = new Map(prev);
        const now = Date.now();
        notificationQueue.forEach(mission => {
          newMap.set(mission.id, now);
        });
        return newMap;
      });

      // Vider la file d'attente complètement
      setNotificationQueue([]);
      setIsShowingMissionNotification(false);
    };

    // Marquer comme en cours
    setCurrentlyDisplayedMissions(prev => {
      const newSet = new Set(prev);
      newSet.add(missionData.id);
      return newSet;
    });

    toast({
      id: toastId,
      position: 'bottom-right',
      duration: 8000, // 8 secondes pour laisser le temps de lire
      isClosable: true,
      containerStyle: { maxWidth: '483px', width: '100%' },
      onCloseComplete: handleClose,
      render: ({ onClose }) => (
        <Box mb={2}>
          <MissionNonAccepteeNotification
            mission={missionData.mission}
            user={missionData.user}
            userType={missionData.userType}
            onClose={() => { onClose(); handleClose(); }}
            onCloseAll={notificationQueue.length > 1 ? () => { onClose(); handleCloseAll(); } : undefined}
            remainingCount={notificationQueue.length - 1}
            onViewMission={() => { navigate(`/admin/missions/${missionData.mission.id}`); onClose(); handleClose(); }}
            onReassignMission={() => { navigate(`/admin/missions/${missionData.mission.id}?reassign=true`); onClose(); handleClose(); }}
          />
        </Box>
      ),
    });

  }, [notificationQueue, isShowingMissionNotification, toast, navigate, location.pathname]);

  // Fonction pour rejoindre automatiquement toutes les missions de l'utilisateur pour les notifications
  const joinUserMissionsForNotifications = async (socketInstance) => {
    try {
      if (!user || !socketInstance) return;

      const userRole = user.rôle || user['rÃ´le'] || user.role;

      let accessibleMissions = [];

      if (userRole === 'admin' || userRole === 'régulateur' || userRole === 'rÃ©gulateur') {
        // Admin/régulateur peuvent accéder à toutes les missions
        const missionsRes = await axios.get(`${BASE_URL}/missions/my-missions`, {
          withCredentials: true
        });
        accessibleMissions = missionsRes.data.map(m => m.id);
      } else {
        // Pour les autres rôles, utiliser la nouvelle route qui filtre côté serveur
        const missionsRes = await axios.get(`${BASE_URL}/missions/my-missions`, {
          withCredentials: true
        });
        accessibleMissions = missionsRes.data.map(m => m.id);
      }

      // Rejoindre chaque mission pour recevoir les notifications
      accessibleMissions.forEach(missionId => {
        socketInstance.emit('join_mission', { missionId });
      });

      // Rejoindre la room utilisateur pour recevoir les notifications personnelles
      socketInstance.emit('join_user_room', { userId: user.id });
    } catch (error) {
      console.error('Navbar: Error joining user missions:', error);
    }
  };

  const playedSoundRef = React.useRef(playedSoundForMessages);
  const userRef = React.useRef(user);

  React.useEffect(() => {
    playedSoundRef.current = playedSoundForMessages;
  }, [playedSoundForMessages]);

  React.useEffect(() => {
    userRef.current = user;
  }, [user]);

  // useEffect pour le socket des notifications en temps réel
  React.useEffect(() => {
    if (!user) return;

    const newSocket = io(getSocketServerUrl(), {
      withCredentials: true,
      transports: ['websocket'], // Utiliser directement websocket pour la rapidité
      upgrade: true,
      reconnection: true,
      reconnectionAttempts: 10, // Plus de tentatives pour la fiabilité
      reconnectionDelay: 1000,
      timeout: 20000 // Augmenter le timeout pour les connexions mobiles lentes
    });

    newSocket.on('connect', () => {
      console.log('Navbar: Socket connected for notifications');
      joinUserMissionsForNotifications(newSocket);
      // Rejoindre aussi les rooms dossier pour recevoir dossier:* en temps réel
      newSocket.emit('join_dossiers');
      newSocket.emit('join_room', 'dossiers');
      newSocket.emit('join_room', { room: 'dossiers' });
    });

    newSocket.on('connect_error', (error) => {
      console.error('Navbar: Socket connection error:', error);
    });

    newSocket.on('disconnect', (reason) => {
      console.log('Navbar: Socket disconnected:', reason);
    });

    // Écouter les nouveaux messages pour mettre à jour les compteurs
    newSocket.on('message', (message) => {
      try {
        const currentUser = userRef.current;
        if (!currentUser) return;

        // Mettre à jour le compteur de messages non lus
        if (message.sender_id !== currentUser.id) {
          setUnreadMessages(prev => ({
            ...prev,
            [message.mission_id]: (prev[message.mission_id] || 0) + 1
          }));

          // Jouer le son de notification si c'est un nouveau message
          if (!playedSoundRef.current.has(message.id)) {
            try {
              notificationSound.currentTime = 0;
              notificationSound.play().catch(error => {
                console.error('Navbar: Error playing notification sound:', error);
              });

              setPlayedSoundForMessages(prev => {
                const newSet = new Set(prev);
                newSet.add(message.id);
                return newSet;
              });
            } catch (error) {
              console.error('Navbar: Error with notification sound:', error);
            }
          }
        }
      } catch (error) {
        console.error('Navbar: Error processing socket message:', error);
      }
    });

    // Écouter les nouvelles notifications
    newSocket.on('new_notification', (notification) => {
      try {
        console.log('Navbar: Received new notification:', notification);

        // Ajouter la notification à l'état des notifications en temps réel
        setRealTimeNotifications(prev => [notification, ...prev]);

        // Incrémenter le compteur d'alertes non lues
        setUnreadAlertes(prev => prev + 1);

        // Jouer le son de notification si l'utilisateur a interagi
        if (hasUserInteracted) {
          try { notificationSound.currentTime = 0; notificationSound.play().catch(() => {}); } catch (_) {}
        }

        // Afficher une notification toast
        toast({
          title: 'Nouvelle alerte',
          description: notification.message,
          status: 'warning',
          duration: 5000,
          isClosable: true,
          position: 'top-right'
        });

      } catch (error) {
        console.error('Navbar: Error processing new notification:', error);
      }
    });

    const extractDossier = (payload) => {
      if (!payload) return null;
      if (payload.dossier) return payload.dossier;
      if (payload.data?.dossier) return payload.data.dossier;
      if (payload.data) return payload.data;
      return payload;
    };

    newSocket.on('dossier:created', (payload) => {
      try {
        const dossier = extractDossier(payload);
        if (!dossier?.id) return;

        if (hasUserInteracted) {
          try { notificationSound.currentTime = 0; notificationSound.play().catch(() => {}); } catch (_) {}
        }

        showDossierBanner(
          `Nouveau dossier #${dossier.id}`,
          'Un dossier de régulation vient d\'être créé.',
          () => navigate(`/admin/dossier-regulation/${dossier.id}`)
        );
      } catch (error) {
        console.error('Navbar: Error dossier:created:', error);
      }
    });

    newSocket.on('dossier:decision-updated', (payload) => {
      try {
        const dossier = extractDossier(payload);
        if (!dossier?.id) return;

        if (hasUserInteracted) {
          try { notificationSound.currentTime = 0; notificationSound.play().catch(() => {}); } catch (_) {}
        }

        showDossierBanner(
          `Décision prise sur dossier #${dossier.id}`,
          'Le médecin a pris une décision, en attente d\'affectation.',
          () => navigate(`/admin/dossier-regulation/${dossier.id}`)
        );
      } catch (error) {
        console.error('Navbar: Error dossier:decision-updated:', error);
      }
    });

    return () => {
      if (newSocket) {
        newSocket.disconnect();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, hasUserInteracted, navigate, notificationSound, showDossierBanner]);

  const userName = contextUserData
    ? `${contextUserData.prenom || contextUserData.firstname || ''} ${contextUserData.nom || contextUserData.lastname || ''}`.trim() || 'User'
    : (user ? `${user.prenom || ''} ${user.nom || ''}`.trim() || 'User' : 'User');

  const userPhoto = contextUserData?.photo || user?.photo;

  const handleLogout = async () => {
    // La fonction logout() appelle le backend pour supprimer le cookie httpOnly
    await logout();
    navigate('/auth/sign-in');
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => setIsFullscreen(true));
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false));
    }
  };

  // Calculer le nombre total de messages non lus
  const totalUnreadMessages = Object.values(unreadMessages).reduce((sum, count) => sum + count, 0);

  // Badge total = messages mission + alertes réclamations non lues
  const totalNotifications = totalUnreadMessages + unreadAlertes;

  // Réinitialiser le compteur d'alertes quand la modale s'ouvre
  const handleOpenNotificationsModal = () => {
    setUnreadAlertes(0);
    onNotificationsModalOpen();
  };

  // Fonction pour marquer les messages comme lus
  const markMessagesAsRead = async (missionId) => {
    // Optimistic update: retire la notif tout de suite
    setUnreadMessages(prev => {
      const newState = { ...prev };
      delete newState[missionId];
      return newState;
    });
    try {
      await markAllMessagesRead(missionId);
    } catch (error) {
      console.error('Error marking messages as read:', error);
      // Optionnel : tu peux remettre la notif si l'API échoue
    }
  };

  const fetchNewMessagesFunc = async () => {
    if (!isConnected && retryCount >= MAX_RETRIES) {
      console.log('Max retries reached, stopping polling');
      return;
    }

    try {
      // 1. Récupérer les missions pour avoir le contexte (ambulanciers, etc.)
      const missionsRes = await getMissions();
      setMissions(missionsRes.data);

      // 2. Récupérer tous les compteurs de messages non lus en UN SEUL appel
      const unreadCountsRes = await getUnreadMessagesCounts();
      const unreadCounts = unreadCountsRes.data; // { missionId: count, ... }

      setUnreadMessages(unreadCounts);

      // 3. Gérer les données complémentaires (ambulanciers) pour les missions ayant des messages non lus
      const missionIdsWithUnread = Object.keys(unreadCounts);

      for (const missionId of missionIdsWithUnread) {
        const mission = missionsRes.data.find(m => m.id === parseInt(missionId));
        if (mission && mission.ambulancier_id && !ambulanciers[mission.ambulancier_id]) {
          try {
            const ambulancierRes = await axios.get(`${BASE_URL}/users/${mission.ambulancier_id}`, {
              withCredentials: true,
              timeout: 5000
            });
            setAmbulanciers(prev => ({
              ...prev,
              [mission.ambulancier_id]: ambulancierRes.data
            }));
          } catch (error) {
            console.error('Navbar: Error fetching ambulancier:', error);
          }
        }
      }

      setIsConnected(true);
      setRetryCount(0);

    } catch (error) {
      console.error('Navbar: Error fetching new messages:', error);

      if (error.code === 'ERR_NETWORK' || error.response?.status === 500 || error.code === 'ECONNABORTED') {
        setIsConnected(false);
        setRetryCount(prev => prev + 1);

        if (retryCount < MAX_RETRIES) {
          setTimeout(() => {
            setIsConnected(true);
          }, RETRY_DELAY);
        }
      }
    }
  };

  // Ajouter cette nouvelle fonction pour vérifier les missions créées
  const checkNewMissions = async () => {
    console.log('checkNewMissions called');
    try {
      const response = await axios.get(`${BASE_URL}/missions`, {
        withCredentials: true,
        timeout: 10000 // 3 second timeout
      });

      // Filtrer uniquement les missions avec statut "créée"
      const createdMissions = response.data.filter(mission => mission.statut === "créée");

      // Pour chaque mission créée
      for (const mission of createdMissions) {
        try {
          // Si la mission n'est pas déjà dans pendingMissions, l'ajouter
          if (!pendingMissions.has(mission.id)) {
            let userData = null;
            let userType = '';

            // Déterminer le type d'utilisateur et récupérer ses informations
            if (mission.type_mission.toLowerCase() === "ambulance" && mission.ambulancier_id) {
              userType = 'ambulancier';
              try {
                const userRes = await axios.get(`${BASE_URL}/users/${mission.ambulancier_id}`, {
                  withCredentials: true,
                  timeout: 10000 // 3 second timeout
                });
                userData = userRes.data;
              } catch (error) {
                console.error(`Error fetching ambulancier data for mission ${mission.id}:`, error);
                continue;
              }
            } else if (mission.type_mission.toLowerCase() === "consultation à domicile" && mission.doctor_id) {
              userType = 'docteur';
              try {
                const userRes = await axios.get(`${BASE_URL}/users/${mission.doctor_id}`, {
                  withCredentials: true,
                  timeout: 10000 // 3 second timeout
                });
                userData = userRes.data;
              } catch (error) {
                console.error(`Error fetching doctor data for mission ${mission.id}:`, error);
                continue;
              }
            } else if (mission.type_mission.toLowerCase() === "acte infirmier" && mission.nurse_id) {
              userType = 'infirmier';
              try {
                const userRes = await axios.get(`${BASE_URL}/users/${mission.nurse_id}`, {
                  withCredentials: true,
                  timeout: 10000 // 3 second timeout
                });
                userData = userRes.data;
              } catch (error) {
                console.error(`Error fetching nurse data for mission ${mission.id}:`, error);
                continue;
              }
            }

            if (userData) {
              // Mettre à jour l'état des utilisateurs
              setAmbulanciers(prev => ({
                ...prev,
                [userData.id]: userData
              }));

              // Ajouter la mission à la liste des missions en attente
              setPendingMissions(prev => {
                const newMap = new Map(prev);
                newMap.set(mission.id, {
                  mission,
                  user: userData,
                  userType
                });
                return newMap;
              });
            }
          }
        } catch (error) {
          console.error(`Error processing mission ${mission.id}:`, error);
          continue;
        }
      }

      const now = new Date();
      const missionsNonAcceptees = [];

      for (const [missionId, missionData] of pendingMissions.entries()) {
        try {
          const missionDate = new Date(missionData.mission.date_mission);
          const timeElapsed = now - missionDate;

          if (timeElapsed >= 60000) { // 1 minute
            // Vérifier si la mission est toujours en statut "créée"
            const missionStatus = response.data.find(m => m.id === missionId)?.statut;

            if (missionStatus === "créée") {
              missionsNonAcceptees.push({
                id: missionId,
                user: missionData.user,
                userType: missionData.userType,
                mission: missionData.mission
              });
            }
            // Retirer la mission de la liste des missions en attente
            setPendingMissions(prev => {
              const newMap = new Map(prev);
              newMap.delete(missionId);
              return newMap;
            });
          }
        } catch (error) {
          console.error(`Error processing pending mission ${missionId}:`, error);
          continue;
        }
      }

      // Nettoyer le bouton s'il n'y a plus de missions non acceptées
      if (missionsNonAcceptees.length === 0) {
        setOtherMissionsButtonTimestamp(null);
        setIsOtherMissionsButtonDisplayed(false);
      }

      // Ne PAS nettoyer agressivement notifiedMissions.
      // Cela évite que les notifications reviennent à cause d'un délai de cache ou d'une erreur réseau temporaire.
      // On garde l'historique des notifications de la session.
      const missionIdsNonAcceptees = new Set(missionsNonAcceptees.map(m => m.id));
      setCurrentlyDisplayedMissions(prev => {
        const newSet = new Set();
        // Garder seulement les missions qui sont toujours non acceptées
        prev.forEach(missionId => {
          if (missionIdsNonAcceptees.has(missionId)) {
            newSet.add(missionId);
          }
        });
        return newSet;
      });


      // Afficher une notification séparée pour chaque mission non acceptée
      if (missionsNonAcceptees.length > 0) {
        try {
          const now = Date.now();
          const TEN_SECONDS = 10000; // 10 secondes (temps avant réaffichage depuis la disparition)

          // Filtrer les missions à notifier :
          // 1. Missions jamais notifiées OU notifiées il y a plus de 15s ET pas actuellement affichées
          const missionsToNotify = missionsNonAcceptees.filter(mission => {
            // Ne pas afficher si déjà en cours d'affichage
            if (currentlyDisplayedMissions.has(mission.id)) {
              return false;
            }

            // Vérifier quand elle a été notifiée pour la dernière fois
            const lastNotified = notifiedMissions.get(mission.id);
            if (lastNotified) {
              const timeSinceLastNotify = now - (typeof lastNotified === 'number' ? lastNotified : 0);
              // Si déjà notifiée il y a moins de 15 secondes, on ignore
              if (timeSinceLastNotify < 15000) {
                return false;
              }
            }

            return true;
          });


          // Enqueue les missions à notifier (on affiche une à la fois)
          if (missionsToNotify.length > 0) {
            // Jouer le son une seule fois pour le nouveau lot
            novalideSound.currentTime = 0;
            novalideSound.play().catch(error => {
              console.error('Error playing novalide sound:', error);
            });

            setNotificationQueue(prev => {
              const existingIds = new Set(prev.map(m => m.id));
              const toAdd = missionsToNotify.filter(m => !existingIds.has(m.id));
              // Limiter l'ajout (sécurité)
              const maxAdd = toAdd.slice(0, 10);
              return [...prev, ...maxAdd];
            });
          }

          // Afficher un bouton pour voir les autres missions si la file contient plus d'une mission
          const queuedCount = notificationQueue.length;
          const totalPending = missionsToNotify.length + queuedCount;
          const remainingCountForButton = Math.max(0, totalPending - 1); // après affichage de la première

          const shouldShowOtherMissionsButton = remainingCountForButton > 0 &&
            !isOtherMissionsButtonDisplayed &&
            (!otherMissionsButtonTimestamp || (now - otherMissionsButtonTimestamp) >= TEN_SECONDS);

          if (shouldShowOtherMissionsButton) {
            setTimeout(() => {
              setIsOtherMissionsButtonDisplayed(true);

              const handleClose = () => {
                setOtherMissionsButtonTimestamp(Date.now());
                setIsOtherMissionsButtonDisplayed(false);
              };

              toast({
                id: 'other-missions-button',
                position: "bottom-right",
                duration: 5000,
                isClosable: true,
                containerStyle: {
                  maxWidth: '483px',
                  width: '100%',
                },
                onCloseComplete: handleClose,
                render: ({ onClose }) => (
                  <Box
                    bg="white"
                    borderRadius="12px"
                    p="10px"
                    boxShadow="0px 4px 20px 0px rgba(0,0,0,0.25)"
                  >
                    <Button
                      w="100%"
                      h="40px"
                      bg="#EFF6FF"
                      border="1px solid rgba(69,156,233,0.2)"
                      borderRadius="6px"
                      onClick={() => {
                        navigate('/admin/missions?filter=non-acceptees');
                        onClose();
                        handleClose();
                      }}
                      _hover={{ bg: '#E0F0FF' }}
                    >
                      <Text
                        fontSize="13px"
                        fontWeight="500"
                        fontFamily="Poppins, sans-serif"
                        color="#459CE9"
                        letterSpacing="-0.13px"
                      >
                        +{remainingCountForButton} autres missions non acceptées
                      </Text>
                    </Button>
                  </Box>
                ),
              });
            }, 500);
          }

          // Nettoyer le bouton s'il n'y a plus de missions supplémentaires
          if (remainingCountForButton === 0) {
            setOtherMissionsButtonTimestamp(null);
            setIsOtherMissionsButtonDisplayed(false);
          }

        } catch (error) {
          console.error('Error enqueueing notifications:', error);
        }
      }
    } catch (error) {
      console.error('Error checking new missions:', error);
      if (error.code === 'ERR_NETWORK' || error.response?.status === 500 || error.code === 'ECONNABORTED') {
        setIsConnected(false);
        setRetryCount(prev => prev + 1);

        if (retryCount < MAX_RETRIES) {
          console.log(`Retrying in ${RETRY_DELAY / 1000} seconds... (Attempt ${retryCount + 1}/${MAX_RETRIES})`);
          setTimeout(() => {
            setIsConnected(true);
            checkNewMissions();
          }, RETRY_DELAY);
        }
      }
    }
  };

  return (
    <>
      {dossierBanner ? (
        <Portal>
          <Box
            position="fixed"
            top="8px"
            left="50%"
            transform="translateX(-50%)"
            w="min(760px, calc(100% - 20px))"
            zIndex={2147483647}
            bg="#B4232D"
            color="white"
            borderRadius="10px"
            border="1px solid rgba(255,255,255,0.25)"
            boxShadow="0 8px 20px rgba(0,0,0,0.25)"
            px="14px"
            py="12px"
          >
            <Flex align="center" justify="space-between" gap="10px">
              <VStack align="start" spacing="2px">
                <Text fontSize="12px" fontWeight="700" letterSpacing="0.3px">
                  ALERTE DOSSIER
                </Text>
                <Text fontSize="16px" fontWeight="700" lineHeight="1.2">
                  {dossierBanner.title}
                </Text>
                <Text fontSize="13px" opacity={0.95}>
                  {dossierBanner.subtitle}
                </Text>
              </VStack>
              <HStack spacing="8px">
                <Button
                  size="sm"
                  variant="ghost"
                  color="white"
                  _hover={{ bg: "rgba(255,255,255,0.12)" }}
                  onClick={() => setDossierBanner(null)}
                >
                  Fermer
                </Button>
                <Button
                  size="sm"
                  bg="white"
                  color="#111827"
                  _hover={{ bg: "#F3F4F6" }}
                  onClick={() => {
                    const onView = dossierBanner.onView;
                    setDossierBanner(null);
                    if (onView) onView();
                  }}
                >
                  VOIR
                </Button>
              </HStack>
            </Flex>
          </Box>
        </Portal>
      ) : null}

      <Flex
        w={{ sm: '100%', md: 'auto' }}
        alignItems="center"
        flexDirection="row"
        gap="10px"
        flexWrap={secondary ? { base: 'wrap', md: 'nowrap' } : 'unset'}
      >
      <SidebarResponsive routes={routes} />

      {/* Widget Téléphone - Connect Phone et Répondre */}
      <PhoneWidget />

      <Button
        p="0px"
        position="relative"
        w="36px"
        h="36px"
        borderRadius="6px"
        border="1px solid #E5E5E5"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="white"
        _hover={{ bg: "gray.50" }}
        onClick={handleOpenNotificationsModal}>
        <Icon
          as={MdNotificationsNone}
          color="#656C7A"
          w="16px"
          h="16px"
        />
        {totalNotifications > 0 && (
          <Flex
            position="absolute"
            top="-2px"
            right="-2px"
            bg="#F8285A"
            color="white"
            borderRadius="full"
            minW="16px"
            h="16px"
            alignItems="center"
            justifyContent="center"
            fontSize="10px"
            fontWeight="bold"
            border="2px solid white"
          >
            {totalNotifications}
          </Flex>
        )}
      </Button>

      <NotificationsModal
        isOpen={isNotificationsModalOpen}
        onClose={onNotificationsModalClose}
        unreadMessages={unreadMessages}
        missions={missions}
        ambulanciers={ambulanciers}
        totalUnreadMessages={totalUnreadMessages}
        markMessagesAsRead={markMessagesAsRead}
        newNotifications={realTimeNotifications}
        userId={user?.id}
      />

      <Button
        variant="ghost"
        bg="white"
        border="1px solid #E5E5E5"
        p="5px"
        minW="36px"
        h="36px"
        borderRadius="6px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        onClick={toggleFullscreen}
        _hover={{ bg: "gray.50" }}>
        <Icon
          h="16px"
          w="16px"
          color="#656C7A"
          as={isFullscreen ? MdFullscreenExit : MdFullscreen}
        />
      </Button>

      <Menu>
        <MenuButton
          p="0px"
          borderRadius="6px"
          overflow="hidden">
          <Img
            _hover={{ cursor: 'pointer' }}
            border="1px solid #006133"
            borderRadius="8px"
            name={userName}
            src={userPhoto}
            bg="#006133"
            w="48px"
            h="48px"
            objectFit="cover"
          />
        </MenuButton>
        <MenuList
          boxShadow="0 10px 40px rgba(0,0,0,0.12)"
          p="0"
          mt="12px"
          borderRadius="20px"
          bg={menuBg}
          border="1px solid"
          borderColor={borderColor}
          minW="250px"
          overflow="hidden"
        >
          {/* Header */}
          <Flex
            direction="column"
            px="20px"
            py="20px"
            borderBottom="1px solid"
            borderColor={borderColor}
            bg={useColorModeValue('gray.50', 'navy.900')}
          >
            <Flex align="center" justify="space-between" mb="8px">
              <Text fontSize="xs" fontWeight="800" color="gray.400" letterSpacing="0.5px">
                SESSIONS ACTIVE
              </Text>
              <Badge
                bg="#006133"
                color="white"
                variant="solid"
                px="10px"
                py="2px"
                borderRadius="8px"
                fontSize="10px"
                fontWeight="700"
                boxShadow="0px 4px 10px rgba(0,0,0,0.05)"
              >
                {contextUserData?.role || 'Utilisateur'}
              </Badge>
            </Flex>
            <Text fontSize="md" fontWeight="700" color={textColor}>
              👋 Bonjour, {userName.split(' ')[0]}
            </Text>
            <Text fontSize="xs" color="gray.500" fontWeight="500">
              Heureux de vous revoir !
            </Text>
          </Flex>

          {/* Items */}
          <Flex direction="column" p="12px" gap="6px">
            <MenuItem
              borderRadius="12px"
              px="14px"
              py="12px"
              transition="all 0.3s ease"
              _hover={{
                bg: useColorModeValue("gray.100", "whiteAlpha.100"),
                transform: "translateX(6px)",
              }}
              _focus={{ bg: useColorModeValue("gray.100", "whiteAlpha.100") }}
              onClick={() => navigate('/admin/profile')}
            >
              <Flex align="center" w="100%">
                <Flex
                  align="center"
                  justify="center"
                  w="34px"
                  h="34px"
                  borderRadius="10px"
                  bg={useColorModeValue("rgba(0, 97, 51, 0.1)", "rgba(0, 97, 51, 0.2)")}
                  color="#006133"
                  mr="12px"
                >
                  <Icon as={MdSettings} h="18px" w="18px" />
                </Flex>
                <Text fontSize="sm" fontWeight="600" color={textColor}>
                  Paramètres du compte
                </Text>
              </Flex>
            </MenuItem>

            <MenuItem
              borderRadius="12px"
              px="14px"
              py="12px"
              transition="all 0.3s ease"
              _hover={{
                bg: "red.50",
                transform: "translateX(6px)",
              }}
              _focus={{ bg: "red.50" }}
              color="red.500"
              onClick={handleLogout}
            >
              <Flex align="center" w="100%">
                <Flex
                  align="center"
                  justify="center"
                  w="34px"
                  h="34px"
                  borderRadius="10px"
                  bg="red.50"
                  color="red.500"
                  mr="12px"
                >
                  <Icon as={MdLogout} h="18px" w="18px" />
                </Flex>
                <Text fontSize="sm" fontWeight="600">
                  Se déconnecter
                </Text>
              </Flex>
            </MenuItem>
          </Flex>
        </MenuList>
      </Menu>
      </Flex>
    </>
  );
}

HeaderLinks.propTypes = {
  variant: PropTypes.string,
  fixed: PropTypes.bool,
  secondary: PropTypes.bool,
  onOpen: PropTypes.func,
};
